import type { DoneEvent, ProgressEvent, ProgressKind, StreamEvent, TurnStatus } from "./types";

/**
 * Parses an SSE byte stream (as produced by the ThunderAy daemon's sseFrame())
 * into a typed StreamEvent async iterable.
 *
 * Chunk-safe: handles frames split across multiple stream chunks.
 * Frame-boundary-safe: splits on \n\n, buffers trailing partial frame.
 * Forward-compat: unknown event names are dropped.
 */
export async function* parseSseStream(
  stream: ReadableStream<Uint8Array>,
): AsyncGenerator<StreamEvent, void, void> {
  const reader = stream.getReader();
  const decoder = new TextDecoder("utf-8", { fatal: false });
  let buffer = "";

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let sep: number;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const ev = parseFrame(frame);
        if (ev) yield ev;
      }
    }
    // Flush any remaining decoder state + final partial frame.
    buffer += decoder.decode();
    const tail = buffer.trim();
    if (tail.length > 0) {
      const ev = parseFrame(tail);
      if (ev) yield ev;
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // ignore
    }
  }
}

/**
 * Closed allowlist: a kind the UI has no glyph or style for renders as an
 * unlabelled blank row, so an unrecognized one is dropped. It is therefore a
 * REGISTRY — a new daemon-side kind is invisible until it is added here, which
 * is exactly how `sibling_conflict` would have shipped dead.
 */
const VALID_KINDS = new Set<string>([
  "tool_use",
  "tool_ok",
  "tool_error",
  "thinking",
  "sibling_conflict",
]);

/**
 * Validate one progress row and stamp the `type` discriminant the app branches
 * on. The daemon never sends `type` — it is this parser's job to add it.
 *
 * Shared by the live `progress` frame and by every row inside a `snapshot`, so
 * the registry cannot enforce itself on one path and not the other.
 */
function parseProgress(value: unknown): ProgressEvent | null {
  if (typeof value !== "object" || value === null) return null;
  const p = value as { kind?: unknown; label?: unknown; meta?: unknown };
  if (typeof p.kind !== "string" || !VALID_KINDS.has(p.kind)) return null;
  if (typeof p.label !== "string") return null;
  return {
    type: "progress",
    kind: p.kind as ProgressKind,
    label: p.label,
    meta: (typeof p.meta === "object" && p.meta !== null ? p.meta : {}) as Record<string, unknown>,
  };
}

function parseFrame(frame: string): StreamEvent | null {
  let eventName = "message";
  const dataLines: string[] = [];

  for (const rawLine of frame.split("\n")) {
    const line = rawLine.replace(/\r$/, "");
    if (line.length === 0 || line.startsWith(":")) continue;
    const colon = line.indexOf(":");
    if (colon === -1) continue;
    const field = line.slice(0, colon);
    let value = line.slice(colon + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    if (field === "event") eventName = value;
    else if (field === "data") dataLines.push(value);
  }

  if (dataLines.length === 0) return null;
  const dataStr = dataLines.join("\n");

  // Only handle daemon-emitted events; drop everything else for forward-compat.
  // Read this as a REGISTRY, not a formality: an unlisted name is dropped with
  // nothing logged anywhere, so a frame the daemon starts sending is invisible
  // until it is added here — which is exactly how `snapshot` would have shipped
  // dead while its server-side test stayed green.
  if (
    eventName !== "token" &&
    eventName !== "done" &&
    eventName !== "error" &&
    eventName !== "progress" &&
    eventName !== "snapshot"
  ) {
    return null;
  }

  // Shown to the member verbatim: friendlyError's `unknown` branch prefers the
  // raw sentence, so this replaced "A request failed. Try again in a moment."
  // on screen and has to be at least as useful. "malformed sse frame" was not.
  const MALFORMED_FRAME_TEXT = "Part of the response could not be read. Send it again.";

  let parsed: unknown;
  try {
    parsed = JSON.parse(dataStr);
  } catch {
    return { type: "error", error: MALFORMED_FRAME_TEXT };
  }

  if (eventName === "token") {
    if (typeof parsed !== "string") return { type: "error", error: MALFORMED_FRAME_TEXT };
    return { type: "token", text: parsed };
  }
  if (eventName === "done") {
    const d = (typeof parsed === "object" && parsed !== null ? parsed : {}) as {
      subtype?: unknown;
      queueNext?: unknown;
      nextItemId?: unknown;
    };
    const ev: DoneEvent = { type: "done" };
    if (typeof d.subtype === "string") ev.subtype = d.subtype;
    // All or nothing: a continuation the client cannot follow is no continuation.
    if (d.queueNext === true && typeof d.nextItemId === "number" && Number.isInteger(d.nextItemId)) {
      ev.queueNext = true;
      ev.nextItemId = d.nextItemId;
    }
    return ev;
  }
  if (eventName === "progress") {
    return parseProgress(parsed);
  }
  if (eventName === "snapshot") {
    // A snapshot REPLACES the consumer's buffer, so a bogus one does not
    // degrade gracefully — it blanks an answer already on screen. A payload
    // that is not an object is dropped rather than coerced into an empty one.
    //
    // Dropping it SILENTLY has a second consumer to answer to. `useChatStream`'s
    // reattach path uses "a snapshot arrived" to tell the turn's own error from
    // this client failing to read it, which is sound only because the daemon
    // enqueues a snapshot before it registers a subscriber (src/active-turn.ts
    // subscribe). A snapshot dropped here breaks that on the client side: the
    // flag never sets, and every later frame — including a real terminal — is
    // discarded as transport noise. Unreachable while the daemon builds the
    // payload from an object literal, which is why this is a note and not a
    // guard; if that ever changes, the two places have to change together.
    if (typeof parsed !== "object" || parsed === null) return null;
    const s = parsed as { content?: unknown; progress?: unknown; status?: unknown; turnId?: unknown; conversationId?: unknown };
    return {
      type: "snapshot",
      content: typeof s.content === "string" ? s.content : "",
      // Every row goes through the SAME validator as a live progress frame, and
      // for the same two reasons. The daemon's `ProgressFrame` is `{kind, label,
      // meta?}` with NO `type` (src/active-turn.ts), and `subscribe` puts those
      // objects into the snapshot verbatim — so a cast here would hand the app
      // rows whose discriminant is `undefined` at runtime while TypeScript
      // swore it was "progress". And a `kind` the UI has no glyph for would
      // arrive intact inside a snapshot while being dropped as a live frame.
      //
      // A malformed list still must not cost the caller the CONTENT, which is
      // the part the user is waiting to read: bad rows drop, content survives.
      progress: Array.isArray(s.progress)
        ? s.progress.map(parseProgress).filter((p): p is ProgressEvent => p !== null)
        : [],
      // "streaming" is the safe default: it keeps the client attached. Falling
      // back to a terminal status would detach it from a turn still generating,
      // which is the truncation this route exists to remove.
      status: (typeof s.status === "string" ? s.status : "streaming") as TurnStatus,
      ...(typeof s.turnId === "number" ? { turnId: s.turnId } : {}),
      ...(typeof s.conversationId === "number" ? { conversationId: s.conversationId } : {}),
    };
  }

  // error
  const err = typeof parsed === "object" && parsed !== null && typeof (parsed as { error?: unknown }).error === "string"
    ? (parsed as { error: string }).error
    : "unknown";
  const code = typeof parsed === "object" && parsed !== null && typeof (parsed as { code?: unknown }).code === "string"
    ? (parsed as { code: string }).code
    : undefined;
  return code !== undefined ? { type: "error", error: err, code } : { type: "error", error: err };
}
